import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Check, Upload, FileText, AlignLeft, Loader2,
  AlertCircle, Type, ChevronDown
} from 'lucide-react';

const FONT_SIZES = [
  { id: 'xs', label: 'Extra Small' },
  { id: 's', label: 'Small' },
  { id: 'm', label: 'Medium' },
  { id: 'l', label: 'Large' },
  { id: 'xl', label: 'Extra Large' }
];

const LINE_HEIGHTS = [
  { id: 'tight', label: 'Tight' },
  { id: 'normal', label: 'Normal' },
  { id: 'relaxed', label: 'Relaxed' }
];

const WINDOW_MODES = [
  { id: 'docked', label: 'Docked Sidebar', description: 'Fixed panel in studio' },
  { id: 'floating', label: 'Floating Window', description: 'Movable & resizable' },
  { id: 'full', label: 'Full-Width Overlay', description: 'Full screen with transparency' }
];

const COUNTDOWN_OPTIONS = [
  { value: 0, label: 'No Countdown' },
  { value: 3, label: '3 seconds' },
  { value: 5, label: '5 seconds' },
  { value: 10, label: '10 seconds' }
];

const FOCUS_LINES_OPTIONS = [
  { value: 2, label: '2 lines' },
  { value: 3, label: '3 lines' },
  { value: 4, label: '4 lines' },
  { value: 5, label: '5 lines' }
];

const ACCEPTED_FORMATS = '.txt,.md,.rtf';

// Custom Select Component - uses portal to avoid overflow clipping
const Select = ({ value, onChange, options, placeholder, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const updatePosition = () => {
      const rect = ref.current.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight && rect.top > spaceBelow;
      setDropdownStyle({
        position: 'fixed', left: rect.left, width: rect.width, zIndex: 99999,
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => { window.removeEventListener('scroll', updatePosition, true); window.removeEventListener('resize', updatePosition); };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value || opt.id === value);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm hover:bg-white/10 transition-colors"
      >
        <span>{selectedOption?.label || selectedOption?.name || placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-[#1a1a2e] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {options.map((option) => (
            <button
              key={option.value ?? option.id}
              onClick={() => { onChange(option.value ?? option.id); setIsOpen(false); }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                (option.value ?? option.id) === value ? 'text-teal-400 bg-white/5' : 'text-gray-300'
              }`}
            >
              {option.label || option.name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// Custom Switch Component
const Switch = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-[#6a1fbf]' : 'bg-white/20'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

// Custom Slider Component
const Slider = ({ value, onChange, min = 0, max = 100, step = 1 }) => (
  <input
    type="range"
    value={value}
    onChange={(e) => onChange(parseFloat(e.target.value))}
    min={min}
    max={max}
    step={step}
    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-teal-500"
  />
);

// Custom Tabs Component
const Tabs = ({ value, onChange, tabs, children }) => (
  <div>
    <div className="flex bg-white/5 rounded-lg p-1 mb-3">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onChange(tab.id)}
          className={`flex-1 flex items-center justify-center gap-2 text-sm py-2 px-3 rounded-md transition-colors ${
            value === tab.id
              ? 'bg-white/10 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
    {children}
  </div>
);

export default function PrompterElementModal({
  isOpen,
  onClose,
  onSave,
  prompter,
  folders,
  currentFolder,
  skipFolderSelect = false,
  readOnly = false,
  onCopyToMyElements
}) {
  const [formData, setFormData] = useState({
    name: '',
    folder_id: currentFolder?.id || '',
    content: '',
    settings: {
      fontSize: 'm',
      lineHeight: 'normal',
      scrollSpeed: 10,
      autoDetectSpeed: false,
      mirroring: false,
      windowMode: 'docked',
      countdownSeconds: 0,
      highlightActiveLine: true,
      focusMode: false,
      focusLines: 3
    }
  });

  const [inputMode, setInputMode] = useState('write');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const initializedRef = useRef(null); // Track which prompter ID we've initialized for

  // Initialize form data only when modal opens or the actual prompter changes (by ID)
  useEffect(() => {
    if (!isOpen) {
      // Reset tracker when modal closes so it re-initializes next time
      initializedRef.current = null;
      return;
    }

    const prompterId = prompter?._id || prompter?.id || null;

    // Skip if we already initialized for this exact prompter
    if (initializedRef.current === (prompterId || '__new__')) return;
    initializedRef.current = prompterId || '__new__';

    if (prompter) {
      // Parse settings properly - handle both string and object formats
      let savedSettings = prompter.settings || {};
      if (typeof savedSettings === 'string') {
        try {
          savedSettings = JSON.parse(savedSettings);
        } catch (e) {
          savedSettings = {};
        }
      }

      setFormData({
        name: prompter.name || '',
        folder_id: prompter.folder_id || currentFolder?.id || '',
        content: prompter.content || prompter.script || '',
        settings: {
          fontSize: 'm',
          lineHeight: 'normal',
          scrollSpeed: 10,
          autoDetectSpeed: false,
          mirroring: false,
          windowMode: 'docked',
          countdownSeconds: 0,
          highlightActiveLine: true,
          focusMode: false,
          focusLines: 3,
          ...savedSettings
        }
      });
    } else {
      // Creating new - reset form with folder
      setFormData({
        name: '',
        folder_id: currentFolder?.id || '',
        content: '',
        settings: {
          fontSize: 'm',
          lineHeight: 'normal',
          scrollSpeed: 10,
          autoDetectSpeed: false,
          mirroring: false,
          windowMode: 'docked',
          countdownSeconds: 0,
          highlightActiveLine: true,
          focusMode: false,
          focusLines: 3
        }
      });
    }
  }, [isOpen, prompter, currentFolder]);

  const updateField = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setIsUploading(true);

    try {
      const text = await file.text();
      // Preserve line breaks and clean up
      const cleanedText = text
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .trim();

      setFormData(prev => ({
        ...prev,
        content: cleanedText,
        name: prev.name || file.name.replace(/\.[^/.]+$/, '')
      }));
      setInputMode('write'); // Switch to write mode to show content
    } catch (err) {
      console.error('File read error:', err);
      setError('Failed to read file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const getWordCount = () => {
    if (!formData.content) return 0;
    return formData.content.trim().split(/\s+/).filter(Boolean).length;
  };

  const getEstimatedDuration = () => {
    const words = getWordCount();
    // Average reading speed: 150 words per minute for teleprompter
    const minutes = words / 150;
    if (minutes < 1) return `${Math.round(minutes * 60)}s`;
    return `${Math.floor(minutes)}m ${Math.round((minutes % 1) * 60)}s`;
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      setError('Please enter a name for the teleprompter.');
      return;
    }
    if (!formData.content.trim()) {
      setError('Please add some script content.');
      return;
    }

    onSave({
      name: formData.name,
      folder_id: formData.folder_id,
      content: formData.content,
      script: formData.content, // Alias for compatibility
      word_count: getWordCount(),
      estimated_duration: getEstimatedDuration(),
      settings: formData.settings
    });
    onClose();
  };

  const isValid = formData.name.trim() && formData.content.trim() && (skipFolderSelect || formData.folder_id);

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#001233] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
                <AlignLeft className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  {prompter ? 'Edit Teleprompter' : 'Create Teleprompter'}
                </h2>
                <p className="text-sm text-gray-400">Add script content for live prompting</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto custom-purple-scroll">
            {readOnly && (
              <div className="flex items-start gap-3 mx-6 mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                <p className="text-sm text-purple-200">
                  This element is from a shared library and is <strong>view only</strong>. Copy it to your library to make changes.
                </p>
              </div>
            )}
            <div className="p-6 space-y-6">
              {/* Error Display */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Teleprompter Name *</label>
                  <input
                    value={formData.name}
                    onChange={(e) => {
                      updateField('name', e.target.value);
                      setError('');
                    }}
                    placeholder="e.g., Opening Remarks"
                    disabled={readOnly}
                    className={`w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>

                {!skipFolderSelect && folders?.length > 0 && (
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Folder *</label>
                    <Select
                      value={formData.folder_id}
                      onChange={(v) => updateField('folder_id', v)}
                      options={folders.map(f => ({ id: f.id, name: f.name }))}
                      placeholder="Select folder..."
                    />
                  </div>
                )}
              </div>

              {/* Script Input */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-white font-medium">Script Content *</label>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>{getWordCount()} words</span>
                    <span>-</span>
                    <span>~{getEstimatedDuration()}</span>
                  </div>
                </div>

                <Tabs
                  value={inputMode}
                  onChange={setInputMode}
                  tabs={[
                    { id: 'write', label: 'Write Script', icon: <Type className="w-4 h-4" /> },
                    { id: 'upload', label: 'Upload File', icon: <Upload className="w-4 h-4" /> }
                  ]}
                >
                  {inputMode === 'write' ? (
                    <textarea
                      value={formData.content}
                      onChange={(e) => updateField('content', e.target.value)}
                      placeholder={`Enter your script here...

Use line breaks to separate paragraphs.
The teleprompter will scroll through this content smoothly.`}
                      disabled={readOnly}
                      className={`w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm font-mono min-h-[200px] resize-y focus:outline-none focus:ring-2 focus:ring-teal-500/50 ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                    />
                  ) : (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_FORMATS}
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      {!readOnly && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          className="w-full border-2 border-dashed border-white/20 rounded-xl p-8 text-center hover:border-teal-500/50 transition-colors disabled:opacity-50"
                        >
                          {isUploading ? (
                            <div className="space-y-2">
                              <Loader2 className="w-8 h-8 text-teal-500 mx-auto animate-spin" />
                              <p className="text-white text-sm">Reading file...</p>
                            </div>
                          ) : (
                            <>
                              <FileText className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                              <p className="text-white text-sm">Click to upload script file</p>
                              <p className="text-xs text-gray-400 mt-1">.txt, .md, .rtf supported</p>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </Tabs>
              </div>

              {/* Settings */}
              <div className="space-y-4">
                <label className="text-white font-medium">Teleprompter Settings</label>

                <div className="grid grid-cols-2 gap-4">
                  {/* Font Size */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Font Size</label>
                    <Select
                      value={formData.settings.fontSize}
                      onChange={(v) => updateField('settings.fontSize', v)}
                      options={FONT_SIZES.map(s => ({ id: s.id, name: s.label }))}
                    />
                  </div>

                  {/* Line Height */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Line Height</label>
                    <Select
                      value={formData.settings.lineHeight}
                      onChange={(v) => updateField('settings.lineHeight', v)}
                      options={LINE_HEIGHTS.map(lh => ({ id: lh.id, name: lh.label }))}
                    />
                  </div>
                </div>

                {/* Auto Detect Speed (AI) */}
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-white text-sm">Auto Detect Speed (AI)</p>
                    <p className="text-xs text-gray-400">AI listens to the speaker and follows along the script</p>
                  </div>
                  <Switch
                    checked={formData.settings.autoDetectSpeed}
                    onChange={(v) => updateField('settings.autoDetectSpeed', v)}
                  />
                </div>

                {/* Scroll Speed - only show when auto detect is off */}
                {!formData.settings.autoDetectSpeed && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-gray-400 text-sm">Scroll Speed</label>
                      <span className="text-sm text-white">{formData.settings.scrollSpeed}</span>
                    </div>
                    <Slider
                      value={formData.settings.scrollSpeed}
                      onChange={(v) => updateField('settings.scrollSpeed', v)}
                      min={0}
                      max={100}
                      step={1}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Slow</span>
                      <span>Fast</span>
                    </div>
                  </div>
                )}

                {/* Window Mode */}
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Display Mode</label>
                  <div className="grid grid-cols-3 gap-2">
                    {WINDOW_MODES.map(mode => (
                      <button
                        key={mode.id}
                        onClick={() => updateField('settings.windowMode', mode.id)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          formData.settings.windowMode === mode.id
                            ? 'border-teal-500 bg-teal-500/20'
                            : 'border-white/10 bg-white/5 hover:bg-white/10'
                        }`}
                      >
                        <p className="text-sm text-white font-medium">{mode.label}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{mode.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Additional Options */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Mirroring */}
                  <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <p className="text-white text-sm">Mirror Text</p>
                      <p className="text-xs text-gray-400">For reflective teleprompters</p>
                    </div>
                    <Switch
                      checked={formData.settings.mirroring}
                      onChange={(v) => updateField('settings.mirroring', v)}
                    />
                  </div>

                  {/* Countdown */}
                  <div>
                    <label className="text-gray-400 text-sm mb-2 block">Countdown Before Start</label>
                    <Select
                      value={formData.settings.countdownSeconds}
                      onChange={(v) => updateField('settings.countdownSeconds', v)}
                      options={COUNTDOWN_OPTIONS}
                    />
                  </div>
                </div>

                {/* Reading Aids (Host Only) */}
                <div className="space-y-3">
                  <label className="text-white font-medium">
                    Reading Aids <span className="text-xs text-gray-400 font-normal">(Host only)</span>
                  </label>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Highlight Active Line */}
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white text-sm">Highlight Active Line</p>
                        <p className="text-xs text-gray-400">Emphasize current reading line</p>
                      </div>
                      <Switch
                        checked={formData.settings.highlightActiveLine}
                        onChange={(v) => updateField('settings.highlightActiveLine', v)}
                      />
                    </div>

                    {/* Focus Mode */}
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white text-sm">Focus Mode</p>
                        <p className="text-xs text-gray-400">Dim text except focus area</p>
                      </div>
                      <Switch
                        checked={formData.settings.focusMode}
                        onChange={(v) => updateField('settings.focusMode', v)}
                      />
                    </div>
                  </div>

                  {/* Focus Lines Count (only show when focus mode is on) */}
                  {formData.settings.focusMode && (
                    <div>
                      <label className="text-gray-400 text-sm mb-2 block">Lines to Show in Focus</label>
                      <Select
                        value={formData.settings.focusLines}
                        onChange={(v) => updateField('settings.focusLines', v)}
                        options={FOCUS_LINES_OPTIONS}
                        className="w-40"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Preview */}
              {formData.content && (
                <div className="space-y-2">
                  <label className="text-white font-medium">Preview</label>
                  <div
                    className={`bg-black/50 rounded-lg p-4 max-h-32 overflow-hidden ${
                      formData.settings.mirroring ? 'transform scale-x-[-1]' : ''
                    }`}
                    style={{
                      fontSize: {
                        xs: '12px',
                        s: '14px',
                        m: '16px',
                        l: '20px',
                        xl: '24px'
                      }[formData.settings.fontSize],
                      lineHeight: {
                        tight: 1.25,
                        normal: 1.5,
                        relaxed: 1.75
                      }[formData.settings.lineHeight]
                    }}
                  >
                    <p className="text-white whitespace-pre-wrap">
                      {formData.content.slice(0, 300)}{formData.content.length > 300 ? '...' : ''}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            {readOnly ? (onCopyToMyElements && (
              <button
                type="button"
                onClick={onCopyToMyElements}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                Copy to My Elements
              </button>
            )) : (
              <button
                onClick={handleSave}
                disabled={!isValid}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-teal-600 hover:to-emerald-700 transition-colors"
              >
                <Check className="w-4 h-4" />
                {prompter ? 'Save Changes' : 'Create Teleprompter'}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
